/**
 * Phase 337 — Email Templates panel for a building.
 *
 * Composes the entire "Email Templates" experience and is reused by both:
 *   • The dedicated `emailTemplates` tab inside BuildingDetails, and
 *   • The "Generate Email" header button (rendered as a dialog).
 *
 * Surface:
 *   - Template list with create / edit / delete / set-default actions.
 *   - Editor for subject + body with a placeholder cheat-sheet and a dynamic
 *     list of named custom fields (the same names render as their input rows
 *     in the right-side composer).
 *   - Composer pane: per-field values, "balance as of" date picker, Preview,
 *     and three send actions — Copy, Mailto, Send via App.
 *
 * The component is intentionally self-contained (own axios calls, own state)
 * so it can be embedded as a tab OR as a dialog without prop drilling.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';

// Phase 713 — Sanitize stored email HTML before rendering so the preview
// matches what Gmail / Outlook display, while blocking XSS payloads.
// Inline `style` + `class` + tables / lists / images / headings / color
// spans / Quill alignment classes are preserved; <script>, event handlers,
// <iframe> and similar dangerous tags are stripped.
const SANITIZE_CFG = {
  ADD_ATTR: ['target', 'rel', 'style', 'class', 'align', 'valign', 'colspan', 'rowspan', 'width', 'height', 'cellspacing', 'cellpadding', 'border', 'bgcolor', 'color', 'face', 'size'],
  ADD_TAGS: ['font'],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'meta', 'link'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onsubmit', 'onchange'],
  ALLOW_DATA_ATTR: false,
};
const sanitizeEmailHtml = (html) => DOMPurify.sanitize(String(html || ''), SANITIZE_CFG);
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import RichEmailEditor from './RichEmailEditor';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Envelope, PlusCircle, PencilSimple, Trash, Copy, PaperPlaneRight,
  Star, FloppyDisk, Paperclip, Eye,
} from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Phase 347 — Pretty-print byte counts for attachment chip labels.
const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

// Phase 345 — Master list of placeholder chips shown in the editor. Custom
// note chips were removed along with the Custom Fields section; the user
// can hide any of the remaining chips per-building via the 'x' affordance.
const PLACEHOLDER_LIST = [
  '{{building_name}}',
  '{{address}}',
  '{{report_date}}',
  '{{cash_balance}}',
  '{{bank_balance}}',
  '{{manager_name}}',
  '{{committee_name}}',
  // Insurance auto-fill
  '{{insurance_company}}',
  '{{insurance_policy_number}}',
  '{{insurance_start_date}}',
  '{{insurance_end_date}}',
  '{{insurance_status}}',
  // Elevator certification auto-fill
  '{{elevator_last_inspection_date}}',
  '{{elevator_next_inspection_date}}',
  '{{elevator_failure_reason}}',
  '{{elevator_notes}}',
  // Phase 349 — Elevator balance auto-fill
  '{{elevator_last_balance}}',
  '{{elevator_statement_balance}}',
  // Fire-extinguisher certification auto-fill
  '{{fire_company_name}}',
  '{{fire_quantity}}',
  '{{fire_unit_price}}',
  '{{fire_total}}',
  '{{fire_contract_number}}',
  '{{fire_contract_start_date}}',
  '{{fire_contract_end_date}}',
  '{{fire_certification_date}}',
  '{{fire_certification_expiry_date}}',
  '{{fire_notes}}',
];

export default function EmailTemplatesPanel({ buildingId, buildingName, embedded = false, manageOnly = false }) {
  const { t } = useLanguage();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');

  // Editor state (mirrors the currently-selected template; null = unsaved blank).
  const [editor, setEditor] = useState({
    name: '', subject: '', body: '', is_default: false, custom_fields: [], category: '',
  });
  // Dirty tracking — disables Save when nothing changed.
  const [dirty, setDirty] = useState(false);

  // Composer state (Phase 345 removed Custom Fields; Phase 347 removed
  // "Balance As Of" — balances are always live values that mirror the
  // Dashboard / Cash & Bank ledgers).

  // Send dialog state.
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendCc, setSendCc] = useState('');
  const [sendBcc, setSendBcc] = useState('');
  const [sending, setSending] = useState(false);

  // Phase 347 — Final confirmation modal (renders the resolved email and the
  // staged attachments BEFORE the actual Brevo call).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSubject, setConfirmSubject] = useState('');
  const [confirmBody, setConfirmBody] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Phase 347 — Staged attachments (File objects). They stay client-side until
  // the user confirms; on Confirm Send we POST them via multipart so they end
  // up both in the actual Brevo email AND in the audit log (Email History).
  const [attachments, setAttachments] = useState([]); // [{file, name, size, type}]

  // Phase 345 — Per-building hidden chips (placeholders + expense categories).
  // Loaded once per building and persisted via the dedicated settings endpoint
  // so curating happens for ALL users of that building, not just this device.
  const [hiddenPlaceholders, setHiddenPlaceholders] = useState([]);
  const [hiddenCategories, setHiddenCategories] = useState([]);

  // Phase 339 — Email History (audit log). Top-level mode toggle between
  // the composer ("compose") and the history table ("history").
  const [activeView, setActiveView] = useState('compose'); // 'compose' | 'history'
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyQuery, setHistoryQuery] = useState('');
  const [historyFrom, setHistoryFrom] = useState('');
  const [historyTo, setHistoryTo] = useState('');
  const [historyStatus, setHistoryStatus] = useState(''); // '' | 'sent' | 'failed'
  const [historyEntry, setHistoryEntry] = useState(null); // currently-viewed log row
  const [historyViewOpen, setHistoryViewOpen] = useState(false);

  // Track which template was rendered into the current preview so re-sends
  // can recover the audit trail (template_id / template_name).
  const [activeTemplateForSend, setActiveTemplateForSend] = useState({ id: '', name: '' });

  // Phase 340 — Available expense categories (only those with at least one
  // record) for the "Insert expense lines" picker. Fetched once per building
  // and re-fetched after a successful send (so freshly-added expenses surface
  // without reloading the page).
  const [expCategories, setExpCategories] = useState([]);
  // Labels for built-in category codes (translated). Falls back to the
  // server-provided `label` when no translation key exists (custom rows).
  const builtinCategoryLabelByCode = useMemo(() => ({
    '201': t('cat_201') || 'Management',
    '202': t('cat_202') || 'Cleaning',
    '203': t('cat_203') || 'Elevator',
    '204': t('cat_204') || 'Electricity',
    '205': t('cat_205') || 'Water',
    '206': t('cat_206') || 'Bank Fees',
    '301': t('cat_301') || 'Elevator Works',
    '302': t('cat_302') || 'Building Works',
    '303': t('cat_303') || 'Plumbing Works',
    '304': t('cat_304') || 'Electrical Works',
    '305': t('cat_305') || 'Mechanical Works',
    '306': t('cat_306') || 'Building Insurance',
    '307': t('cat_307') || 'Garden Works',
    '308': t('cat_308') || 'Small Expenses',
    '309': t('cat_309') || 'Bank Fees',
    '310': t('cat_310') || 'Elevator Certification',
  }), [t]);

  const fetchTemplates = useCallback(async () => {
    // Phase 349 — Templates are global. We use the global endpoint when
    // `manageOnly` is set OR when no building context is available so the
    // editor page works standalone.
    if (!manageOnly && !buildingId) return;
    setLoading(true);
    try {
      const url = (manageOnly || !buildingId)
        ? `${API}/email-templates`
        : `${API}/buildings/${buildingId}/email-templates`;
      const res = await axios.get(url);
      const rows = Array.isArray(res.data) ? res.data : [];
      setTemplates(rows);
      // Auto-select default → first, if none yet selected.
      if (rows.length > 0 && !selectedId) {
        const def = rows.find(r => r.is_default) || rows[0];
        setSelectedId(def.template_id);
        setEditor({
          name: def.name || '',
          subject: def.subject || '',
          body: def.body || '',
          is_default: !!def.is_default,
          custom_fields: Array.isArray(def.custom_fields) ? def.custom_fields : [],
          category: def.category || '',
        });
        setDirty(false);
      }
    } catch (e) {
      toast.error(t('errorOccurred') || 'Error');
    } finally {
      setLoading(false);
    }
  }, [buildingId, selectedId, manageOnly, t]);

  useEffect(() => { fetchTemplates(); /* eslint-disable-next-line */ }, [buildingId, manageOnly]);

  // Phase 340 — Fetch available expense categories. Only categories with at
  // least one expense are returned; the picker hides the rest automatically.
  const fetchExpenseCategories = useCallback(async () => {
    if (!buildingId) return;
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/email-template-expense-categories`);
      setExpCategories(Array.isArray(res.data?.categories) ? res.data.categories : []);
    } catch {
      setExpCategories([]);
    }
  }, [buildingId]);
  useEffect(() => { fetchExpenseCategories(); }, [fetchExpenseCategories]);

  // Phase 345 — Per-building hidden chips. Fetch once on mount so the editor
  // immediately reflects what's been curated.
  const fetchHiddenTags = useCallback(async () => {
    if (!buildingId) return;
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/email-template-settings`);
      setHiddenPlaceholders(Array.isArray(res.data?.hidden_placeholders) ? res.data.hidden_placeholders : []);
      setHiddenCategories(Array.isArray(res.data?.hidden_categories) ? res.data.hidden_categories : []);
    } catch {
      setHiddenPlaceholders([]);
      setHiddenCategories([]);
    }
  }, [buildingId]);
  useEffect(() => { fetchHiddenTags(); }, [fetchHiddenTags]);

  // Persist a new hidden-tags state to the backend so all users see the same
  // curated chip set. Returns the canonical arrays the server stored.
  const persistHiddenTags = useCallback(async (next) => {
    if (!buildingId) return;
    try {
      const res = await axios.put(`${API}/buildings/${buildingId}/email-template-settings`, next);
      setHiddenPlaceholders(Array.isArray(res.data?.hidden_placeholders) ? res.data.hidden_placeholders : []);
      setHiddenCategories(Array.isArray(res.data?.hidden_categories) ? res.data.hidden_categories : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Error');
    }
  }, [buildingId, t]);

  const hidePlaceholder = (token) => {
    if (!token) return;
    const next = Array.from(new Set([...(hiddenPlaceholders || []), token]));
    setHiddenPlaceholders(next);
    persistHiddenTags({ hidden_placeholders: next, hidden_categories: hiddenCategories });
  };
  const hideCategory = (slug) => {
    if (!slug) return;
    const next = Array.from(new Set([...(hiddenCategories || []), slug]));
    setHiddenCategories(next);
    persistHiddenTags({ hidden_placeholders: hiddenPlaceholders, hidden_categories: next });
  };
  const restoreHiddenTags = async () => {
    if (!hiddenPlaceholders.length && !hiddenCategories.length) return;
    if (!window.confirm(t('confirmRestoreHiddenTags') || 'Restore all hidden placeholders and categories?')) return;
    await persistHiddenTags({ hidden_placeholders: [], hidden_categories: [] });
  };

  // Phase 339 — Email History fetch. Server-side filters for date range and
  // status; free-text `q` is applied on the server too so the table reflects
  // the user's filter in a single round-trip.
  const fetchHistory = useCallback(async () => {
    if (!buildingId) return;
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (historyQuery.trim()) params.set('q', historyQuery.trim());
      if (historyFrom) params.set('date_from', historyFrom);
      if (historyTo) params.set('date_to', historyTo);
      if (historyStatus) params.set('status', historyStatus);
      const url = `${API}/buildings/${buildingId}/email-history${params.toString() ? `?${params}` : ''}`;
      const res = await axios.get(url);
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(t('errorOccurred') || 'Error');
    } finally {
      setHistoryLoading(false);
    }
  }, [buildingId, historyQuery, historyFrom, historyTo, historyStatus, t]);

  // Auto-refresh history whenever we switch into it OR a filter changes
  // (debounced so the user can type freely).
  useEffect(() => {
    if (activeView !== 'history') return;
    const timer = setTimeout(fetchHistory, 250);
    return () => clearTimeout(timer);
  }, [activeView, fetchHistory]);

  // When the selectedId changes, load that template into editor.
  useEffect(() => {
    if (!selectedId) return;
    const found = templates.find(r => r.template_id === selectedId);
    if (found) {
      setEditor({
        name: found.name || '',
        subject: found.subject || '',
        body: found.body || '',
        is_default: !!found.is_default,
        custom_fields: Array.isArray(found.custom_fields) ? found.custom_fields : [],
        category: found.category || '',
      });
      setDirty(false);
    }
  }, [selectedId, templates]);

  const startNew = () => {
    setSelectedId('');
    setEditor({ name: '', subject: '', body: '', is_default: false, custom_fields: [], category: '' });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!editor.name.trim()) {
      toast.error(t('templateNameRequired') || 'Template name is required');
      return;
    }
    try {
      if (selectedId) {
        const res = await axios.put(`${API}/email-templates/${selectedId}`, editor);
        toast.success(t('savedSuccess') || 'Saved');
        setDirty(false);
        await fetchTemplates();
        setSelectedId(res.data?.template_id || selectedId);
      } else {
        // Phase 349 — Templates are global; prefer the global endpoint when
        // no building context (manage page) and gracefully fall back to the
        // building alias for legacy callers.
        const url = (manageOnly || !buildingId)
          ? `${API}/email-templates`
          : `${API}/buildings/${buildingId}/email-templates`;
        const res = await axios.post(url, editor);
        toast.success(t('savedSuccess') || 'Saved');
        setDirty(false);
        await fetchTemplates();
        setSelectedId(res.data?.template_id || '');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Error');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm(t('confirmDeleteTemplate') || 'Delete this template?')) return;
    try {
      await axios.delete(`${API}/email-templates/${templateId}`);
      toast.success(t('deletedSuccess') || 'Deleted');
      if (selectedId === templateId) {
        setSelectedId('');
        startNew();
      }
      await fetchTemplates();
    } catch (e) {
      toast.error(t('errorOccurred') || 'Error');
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      await axios.put(`${API}/email-templates/${templateId}`, { is_default: true });
      toast.success(t('savedSuccess') || 'Saved');
      await fetchTemplates();
    } catch (e) {
      toast.error(t('errorOccurred') || 'Error');
    }
  };

  // Update a single editor field and flag dirty.
  const updateEditor = (patch) => {
    setEditor(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  // Custom fields management removed in Phase 345 — the section was deleted
  // from the UI per the user's request, so addCustomField / renameCustomField
  // / removeCustomField are no longer needed.

  // Phase 345 — Render the template on demand (used by Copy / Mailto / Send).
  // We no longer keep a live preview pane; instead the actions resolve the
  // placeholders at the moment they are needed so the final email reflects
  // the latest figures from the canonical ledger.
  const renderNow = useCallback(async () => {
    if (!buildingId) return { subject: editor.subject || '', body: editor.body || '' };
    try {
      const res = await axios.post(`${API}/buildings/${buildingId}/email-templates/render`, {
        subject: editor.subject,
        body: editor.body,
        custom_fields: {},
      });
      return { subject: res.data?.subject || '', body: res.data?.body || '' };
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Render failed');
      return null;
    }
  }, [buildingId, editor.subject, editor.body, t]);

  const insertPlaceholder = (token) => {
    // Append to body at the end — simple but reliable across browsers.
    updateEditor({ body: (editor.body || '') + token });
  };

  // Phase 340 — One-click "Insert expense block" for a given category slug.
  // Appends a readable line carrying all three latest-* placeholders so the
  // manager doesn't have to remember the exact placeholder strings.
  const insertExpenseBlock = (cat) => {
    if (!cat?.slug) return;
    const label = cat.is_builtin ? (builtinCategoryLabelByCode[cat.code] || cat.label || cat.slug) : (cat.label || cat.slug);
    const line = `${label}: {{expense:${cat.slug}:last_date}} — {{expense:${cat.slug}:last_amount}}`;
    const existing = editor.body || '';
    const sep = (existing && !existing.endsWith('\n')) ? '\n' : '';
    updateEditor({ body: existing + sep + line });
  };

  // Phase 340 — Bulk-insert blocks for every category (or a chosen subset)
  // in one go. Lines are sorted by category code so they read consistently.
  const insertAllExpenseBlocks = () => {
    if (!expCategories || expCategories.length === 0) return;
    const sorted = [...expCategories].sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    const lines = sorted.map(cat => {
      const label = cat.is_builtin ? (builtinCategoryLabelByCode[cat.code] || cat.label || cat.slug) : (cat.label || cat.slug);
      return `${label}: {{expense:${cat.slug}:last_date}} — {{expense:${cat.slug}:last_amount}}`;
    });
    const existing = editor.body || '';
    const sep = (existing && !existing.endsWith('\n')) ? '\n' : '';
    updateEditor({ body: existing + sep + lines.join('\n') });
  };

  // ====== Three actions: Copy, Mailto, Send via App ======
  const handleCopy = async () => {
    const r = await renderNow();
    if (!r) return;
    const text = `Subject: ${r.subject}\n\n${r.body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('copiedToClipboard') || 'Copied to clipboard');
    } catch {
      toast.error(t('errorOccurred') || 'Copy failed');
    }
  };

  const handleMailto = async () => {
    const r = await renderNow();
    if (!r) return;
    const subj = encodeURIComponent(r.subject || '');
    const body = encodeURIComponent(r.body || '');
    // Note: mailto: bodies have ~2000 char limits in many clients but we
    // preserve full content; clients that truncate will still open with the
    // first chunk and the user can paste the rest from clipboard.
    window.location.href = `mailto:?subject=${subj}&body=${body}`;
  };

  // Phase 347 — Send flow now has TWO stages:
  //   1. Send dialog → user enters / edits recipients + attaches files.
  //   2. Confirm dialog → renders subject + body with placeholders RESOLVED,
  //      lists the recipients + every attachment, and ONLY THEN fires the
  //      actual POST.  This lets the user double-check the live data the
  //      committee will see before the Brevo call leaves the building.

  // Stage 1 → Stage 2 transition.
  const openConfirm = async () => {
    if (!sendTo.trim()) {
      toast.error(t('atLeastOneRecipient') || 'At least one recipient is required');
      return;
    }
    setConfirmLoading(true);
    setConfirmOpen(true);
    setConfirmSubject('');
    setConfirmBody('');
    try {
      const r = await renderNow();
      if (!r) {
        setConfirmOpen(false);
        return;
      }
      setConfirmSubject(r.subject || '');
      setConfirmBody(r.body || '');
    } finally {
      setConfirmLoading(false);
    }
  };

  // Stage 2 → actually POST. Uses multipart so attachments ride with the
  // request and end up both in Brevo AND in the audit log.
  const confirmAndSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      const toArr = sendTo.split(',').map(s => s.trim()).filter(Boolean);
      const ccArr = sendCc.split(',').map(s => s.trim()).filter(Boolean);
      const bccArr = sendBcc.split(',').map(s => s.trim()).filter(Boolean);
      const tpl = templates.find(r2 => r2.template_id === selectedId);
      const payload = {
        subject: confirmSubject,
        body: confirmBody,
        to: toArr,
        cc: ccArr,
        bcc: bccArr,
        template_id: activeTemplateForSend.id || tpl?.template_id || '',
        template_name: activeTemplateForSend.name || tpl?.name || editor.name || '',
      };
      const form = new FormData();
      form.append('payload', JSON.stringify(payload));
      (attachments || []).forEach((a) => {
        if (a?.file) form.append('attachments', a.file, a.name || a.file.name);
      });
      await axios.post(`${API}/buildings/${buildingId}/email-templates/send`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('emailSent') || 'Email sent');
      setConfirmOpen(false);
      setSendOpen(false);
      setSendTo(''); setSendCc(''); setSendBcc('');
      setAttachments([]);
      setActiveTemplateForSend({ id: '', name: '' });
      if (activeView === 'history') fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  // Attachment helpers — staged client-side until Confirm Send.
  const addAttachments = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setAttachments(prev => [
      ...prev,
      ...incoming.map(f => ({ file: f, name: f.name, size: f.size, type: f.type })),
    ]);
  };
  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };
  const replaceAttachment = (idx, file) => {
    if (!file) return;
    setAttachments(prev => prev.map((a, i) => i === idx ? { file, name: file.name, size: file.size, type: file.type } : a));
  };

  // Phase 339 — Re-send an existing history entry. Loads its subject/body/
  // recipients into the send dialog (no re-render — we send the body
  // byte-identical to what was originally sent).
  const handleResend = (row) => {
    if (!row) return;
    setActiveTemplateForSend({ id: row.template_id || '', name: row.template_name || '' });
    setSendTo((row.to_list || []).join(', ') || row.to_email || '');
    setSendCc((row.cc_list || []).join(', ') || row.cc || '');
    setSendBcc((row.bcc_list || []).join(', ') || row.bcc || '');
    // Stash the historical body/subject in the editor so the renderer can
    // be bypassed at send time (we send what was originally sent).
    if (row.subject || row.body) {
      setEditor(prev => ({ ...prev, subject: row.subject || prev.subject, body: row.body || prev.body }));
    }
    setHistoryViewOpen(false);
    setActiveView('compose');
    setSendOpen(true);
  };

  // ====== Render ======

  // Phase 345 — Visible chip lists (after applying per-building hidden tags).
  const visiblePlaceholders = useMemo(
    () => PLACEHOLDER_LIST.filter(p => !(hiddenPlaceholders || []).includes(p)),
    [hiddenPlaceholders],
  );
  const visibleExpCategories = useMemo(
    () => (expCategories || []).filter(c => !(hiddenCategories || []).includes(c.slug)),
    [expCategories, hiddenCategories],
  );
  const hasHidden = (hiddenPlaceholders.length + hiddenCategories.length) > 0;

  return (
    <div className="space-y-4" data-testid="email-templates-panel">
      {!embedded && (
        <div className="flex items-center gap-2 mb-1">
          <Envelope size={18} weight="fill" className="text-zinc-700" />
          <h2 className="text-base font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('emailTemplates') || 'Email Templates'}
          </h2>
          <span className="text-xs text-zinc-500">— {buildingName}</span>
        </div>
      )}

      {/* Phase 339 — Top-level view toggle. Same panel hosts the composer
          and the audit log so the user never leaves the "Email Templates"
          context. */}
      <div className="flex border-b border-zinc-200" data-testid="email-view-tabs">
        <button
          type="button"
          onClick={() => setActiveView('compose')}
          className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${activeView === 'compose' ? 'border-b-2 border-zinc-900 text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
          data-testid="email-view-compose-tab"
        >
          {t('compose') || 'Compose'}
        </button>
        <button
          type="button"
          onClick={() => setActiveView('history')}
          className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${activeView === 'history' ? 'border-b-2 border-zinc-900 text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'} ${manageOnly ? 'hidden' : ''}`}
          data-testid="email-view-history-tab"
        >
          {t('emailHistory') || 'Email History'}
        </button>
      </div>

      {activeView === 'compose' && (
      // Phase 347 — Templates list moved out of a dedicated left column into
      // a Select dropdown in the editor header (saves screen real estate so
      // the body editor can take the FULL width).
      <div className="grid grid-cols-1 gap-4">
        {/* ============== Editor (full width) ============== */}
        <div className="border border-zinc-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-zinc-200 bg-zinc-50">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600 flex items-center gap-1">
                <PencilSimple size={12} /> {t('editor') || 'Editor'}
              </span>
              {/* Phase 347 — Template picker dropdown (replaces the left column). */}
              <select
                value={selectedId}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__new__') { startNew(); return; }
                  setSelectedId(v);
                }}
                className="rounded-none border border-zinc-200 bg-white text-sm px-2 py-1 min-w-[200px]"
                data-testid="email-tpl-picker"
                title={t('templates') || 'Templates'}
              >
                <option value="">{t('selectTemplate') || 'Select template…'}</option>
                {templates.map(tpl => (
                  <option key={tpl.template_id} value={tpl.template_id}>
                    {tpl.is_default ? '★ ' : ''}{tpl.name}
                  </option>
                ))}
                <option value="__new__">＋ {t('newTemplate') || 'New template'}</option>
              </select>
              {selectedId && (
                <>
                  {!templates.find(r => r.template_id === selectedId)?.is_default && (
                    <button
                      type="button"
                      onClick={() => handleSetDefault(selectedId)}
                      className="text-xs px-1.5 py-1 border border-zinc-200 text-zinc-500 hover:bg-white"
                      title={t('setAsDefault') || 'Set as default'}
                      data-testid={`email-tpl-setdefault-${selectedId}`}
                    >
                      <Star size={12} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(selectedId)}
                    className="text-xs px-1.5 py-1 border border-zinc-200 text-zinc-500 hover:bg-white hover:text-red-600"
                    title={t('delete') || 'Delete'}
                    data-testid={`email-tpl-delete-${selectedId}`}
                  >
                    <Trash size={12} />
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {/* Phase 349 — Send / Copy / Mailto hidden in manage-only mode
                  (global template editor at Settings → Email Templates). The
                  building's "Emails" tab is the entry point for sending. */}
              {!manageOnly && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopy}
                    className="rounded-none h-7 text-xs"
                    disabled={!(editor.subject || editor.body)}
                    data-testid="email-tpl-copy-btn"
                    title={t('copyResolved') || 'Copy resolved content'}
                  >
                    <Copy size={14} className="mr-1" /> {t('copy') || 'Copy'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleMailto}
                    className="rounded-none h-7 text-xs"
                    disabled={!(editor.subject || editor.body)}
                    data-testid="email-tpl-mailto-btn"
                  >
                    <Envelope size={14} className="mr-1" /> Mailto
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setSendOpen(true)}
                    className="rounded-none h-7 text-xs bg-zinc-900 hover:bg-zinc-800"
                    disabled={!(editor.subject || editor.body)}
                    data-testid="email-tpl-send-btn"
                  >
                    <PaperPlaneRight size={14} className="mr-1" /> {t('send') || 'Send'}
                  </Button>
                </>
              )}
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!dirty}
                className="rounded-none h-7 text-xs bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-300"
                data-testid="email-tpl-save-btn"
              >
                <FloppyDisk size={14} className="mr-1" />
                {t('save') || 'Save'}
              </Button>
            </div>
          </div>
          <div className="p-3 space-y-3">
            {/* Phase 347 — Template Name stacked above Subject on the LEFT;
                Balance As Of removed (balances are always live). */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">{t('templateName') || 'Template Name'}</Label>
                  <Input
                    value={editor.name}
                    onChange={(e) => updateEditor({ name: e.target.value })}
                    className="rounded-none mt-1"
                    placeholder={t('templateNamePh') || 'e.g. Monthly Report'}
                    data-testid="email-tpl-name-input"
                  />
                </div>
                {/* Phase 960 — Category selector groups templates in
                    the Email Center picker (Statements / Meetings /
                    Financial / Other). Optional; blank = Other. */}
                <div>
                  <Label className="text-xs">{t('category') || 'Category'}</Label>
                  <select
                    value={editor.category || ''}
                    onChange={(e) => updateEditor({ category: e.target.value })}
                    className="w-full rounded-none border border-zinc-200 bg-white text-sm px-2 py-2 mt-1"
                    data-testid="email-tpl-category-input"
                  >
                    <option value="">{t('emailCatOther') || 'Other'}</option>
                    <option value="statements">{t('emailCatStatements') || 'Statements'}</option>
                    <option value="meetings">{t('emailCatMeetings') || 'Meetings'}</option>
                    <option value="financial">{t('emailCatFinancial') || 'Financial Information'}</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">{t('subject') || 'Subject'}</Label>
                  <Input
                    value={editor.subject}
                    onChange={(e) => updateEditor({ subject: e.target.value })}
                    className="rounded-none mt-1 font-mono text-sm"
                    placeholder="Report for {{building_name}} – {{report_date}}"
                    data-testid="email-tpl-subject-input"
                  />
                </div>
              </div>
              <div className="text-[11px] text-zinc-400 italic md:pl-4 md:border-l md:border-zinc-100 flex items-center">
                {t('liveBalancesNote') || 'Cash & Bank balance placeholders always pull the LIVE figures (same as the Dashboard).'}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">{t('body') || 'Body'}</Label>
                <span className="text-[10px] text-zinc-400">{(editor.body || '').length} {t('characters') || 'chars'}</span>
              </div>
              <div className="mt-1">
                <RichEmailEditor
                  value={editor.body || ''}
                  onChange={(html) => updateEditor({ body: html })}
                  placeholder={"Hello,\n\nMonthly report for {{building_name}}.\nCash: {{cash_balance}}\nBank: {{bank_balance}}\n\n{{manager_name}}"}
                  minHeight="500px"
                  testId="email-tpl-body-input"
                />
              </div>
            </div>
        {/* Placeholder cheat-sheet (click to insert; 'x' to hide). */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">{t('availablePlaceholders') || 'Available Placeholders'}</Label>
                {hasHidden && (
                  <button
                    type="button"
                    onClick={restoreHiddenTags}
                    className="text-[10px] underline text-zinc-500 hover:text-zinc-900"
                    data-testid="email-tpl-restore-hidden"
                    title={t('restoreHiddenTags') || 'Restore hidden chips'}
                  >
                    {t('restoreHiddenTags') || 'Restore hidden'} ({hiddenPlaceholders.length + hiddenCategories.length})
                  </button>
                )}
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {visiblePlaceholders.map(p => (
                  <span
                    key={p}
                    className="inline-flex items-stretch border border-zinc-200 bg-zinc-50 text-zinc-700"
                    data-testid={`email-tpl-ph-chip-${p.replace(/[{}]/g, '')}`}
                  >
                    <button
                      type="button"
                      onClick={() => insertPlaceholder(p)}
                      className="text-[10px] font-mono px-1.5 py-0.5 hover:bg-zinc-100"
                      data-testid={`email-tpl-ph-${p.replace(/[{}]/g, '')}`}
                    >
                      {p}
                    </button>
                    <button
                      type="button"
                      onClick={() => hidePlaceholder(p)}
                      className="px-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 border-l border-zinc-200"
                      data-testid={`email-tpl-ph-hide-${p.replace(/[{}]/g, '')}`}
                      title={t('hide') || 'Hide'}
                      aria-label={t('hide') || 'Hide'}
                    >
                      <Trash size={10} />
                    </button>
                  </span>
                ))}
                {visiblePlaceholders.length === 0 && (
                  <span className="text-[11px] text-zinc-400 italic">
                    {t('allPlaceholdersHidden') || 'All placeholders hidden.'}
                  </span>
                )}
              </div>
            </div>

            {/* Phase 340 — Expense categories picker. Only categories with at
                least one expense show up; clicking one appends a labeled line
                with all three placeholders ({{expense:<slug>:last_date}}…) to
                the body. Phase 345: each chip has an 'x' to hide it per-building. */}
            {expCategories.length > 0 && (
              <div data-testid="email-tpl-expense-picker">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">{t('expenseCategories') || 'Expense Categories'}</Label>
                  <button
                    type="button"
                    onClick={insertAllExpenseBlocks}
                    className="text-[10px] underline text-zinc-500 hover:text-zinc-900"
                    data-testid="email-tpl-expense-insert-all"
                    title={t('insertAllCategories') || 'Insert all'}
                  >
                    + {t('insertAll') || 'Insert all'}
                  </button>
                </div>
                <p className="text-[10px] text-zinc-400 italic mb-1.5">
                  {t('expenseCategoriesHint') || 'Click to insert the latest entry for each category. Categories without records are hidden.'}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {visibleExpCategories.map(cat => {
                    const label = cat.is_builtin ? (builtinCategoryLabelByCode[cat.code] || cat.label || cat.slug) : (cat.label || cat.slug);
                    return (
                      <span
                        key={cat.slug || cat.code}
                        className="inline-flex items-stretch border border-zinc-200 bg-emerald-50 text-emerald-800"
                        data-testid={`email-tpl-expcat-chip-${cat.slug}`}
                      >
                        <button
                          type="button"
                          onClick={() => insertExpenseBlock(cat)}
                          className="text-[10px] px-2 py-1 hover:bg-emerald-100 transition-colors text-left"
                          data-testid={`email-tpl-expcat-${cat.slug}`}
                          title={`${label} — ${cat.last_date} — ${cat.last_amount}`}
                        >
                          <div className="font-semibold">{label}</div>
                          <div className="font-mono text-[9px] text-emerald-700 opacity-80">
                            {cat.last_date} · {cat.last_amount}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => hideCategory(cat.slug)}
                          className="px-1 text-emerald-700/60 hover:text-red-600 hover:bg-red-50 border-l border-emerald-200"
                          data-testid={`email-tpl-expcat-hide-${cat.slug}`}
                          title={t('hide') || 'Hide'}
                          aria-label={t('hide') || 'Hide'}
                        >
                          <Trash size={10} />
                        </button>
                      </span>
                    );
                  })}
                  {visibleExpCategories.length === 0 && (
                    <span className="text-[11px] text-zinc-400 italic">
                      {t('allCategoriesHidden') || 'All categories hidden.'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ============== HISTORY VIEW (Phase 339) ============== */}
      {activeView === 'history' && (
        <div className="border border-zinc-200 bg-white" data-testid="email-history-panel">
          {/* Filters bar */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 p-3 border-b border-zinc-200 bg-zinc-50">
            <Input
              type="search"
              value={historyQuery}
              onChange={(e) => setHistoryQuery(e.target.value)}
              placeholder={t('searchHistoryPh') || 'Search subject, body, recipient, template…'}
              className="rounded-none"
              data-testid="email-history-search"
            />
            <Input
              type="date"
              value={historyFrom}
              onChange={(e) => setHistoryFrom(e.target.value)}
              className="rounded-none"
              data-testid="email-history-from"
            />
            <Input
              type="date"
              value={historyTo}
              onChange={(e) => setHistoryTo(e.target.value)}
              className="rounded-none"
              data-testid="email-history-to"
            />
            <select
              value={historyStatus}
              onChange={(e) => setHistoryStatus(e.target.value)}
              className="rounded-none border border-zinc-200 bg-white text-sm px-3 py-2"
              data-testid="email-history-status"
            >
              <option value="">{t('allStatuses') || 'All statuses'}</option>
              <option value="sent">{t('sent') || 'Sent'}</option>
              <option value="failed">{t('failed') || 'Failed'}</option>
            </select>
          </div>

          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm" data-testid="email-history-table">
              <thead className="bg-zinc-50 sticky top-0">
                <tr className="border-b border-zinc-200">
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{t('sentAt') || 'Sent At'}</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{t('subject') || 'Subject'}</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{t('recipients') || 'Recipients'}</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{t('template') || 'Template'}</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{t('sentBy') || 'Sent By'}</th>
                  <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{t('status') || 'Status'}</th>
                  <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{t('actions') || 'Actions'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {historyLoading && (
                  <tr><td colSpan={7} className="px-3 py-6 text-xs text-zinc-500 text-center">{t('loading') || 'Loading…'}</td></tr>
                )}
                {!historyLoading && history.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-xs text-zinc-500 text-center italic">{t('noEmailHistory') || 'No emails sent yet.'}</td></tr>
                )}
                {!historyLoading && history.map((row) => {
                  const recipients = (row.to_list && row.to_list.length > 0) ? row.to_list : (row.to_email || '').split(',').map(s => s.trim()).filter(Boolean);
                  const sentDate = row.sent_at ? new Date(row.sent_at) : null;
                  const dateStr = sentDate ? `${String(sentDate.getDate()).padStart(2, '0')}/${String(sentDate.getMonth() + 1).padStart(2, '0')}/${sentDate.getFullYear()}` : '—';
                  const timeStr = sentDate ? `${String(sentDate.getHours()).padStart(2, '0')}:${String(sentDate.getMinutes()).padStart(2, '0')}` : '';
                  return (
                    <tr
                      key={row.log_id}
                      className="hover:bg-zinc-50 cursor-pointer"
                      onClick={() => { setHistoryEntry(row); setHistoryViewOpen(true); }}
                      data-testid={`email-history-row-${row.log_id}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs text-zinc-600 whitespace-nowrap">
                        <div>{dateStr}</div>
                        <div className="text-[10px] text-zinc-400">{timeStr}</div>
                      </td>
                      <td className="px-3 py-2 text-zinc-800 max-w-xs">
                        <div className="truncate font-medium" title={row.subject}>{row.subject || '—'}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-600">
                        <span className="font-mono">{recipients.slice(0, 2).join(', ')}</span>
                        {recipients.length > 2 && <span className="text-zinc-400"> +{recipients.length - 2}</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-600 truncate max-w-[160px]" title={row.template_name || ''}>
                        {row.template_name || <span className="text-zinc-400 italic">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-600 truncate max-w-[160px]">{row.sent_by || '—'}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[10px] px-1.5 py-0.5 border font-semibold uppercase ${row.status === 'sent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                          {row.status || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleResend(row); }}
                          className="text-xs px-2 py-1 border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                          title={t('resend') || 'Re-send'}
                          data-testid={`email-history-resend-${row.log_id}`}
                        >
                          <PaperPlaneRight size={12} className="inline mr-1" /> {t('resend') || 'Re-send'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Phase 339 — History view modal: shows the email exactly as it was sent. */}
      <Dialog open={historyViewOpen} onOpenChange={(o) => { if (!o) { setHistoryViewOpen(false); setHistoryEntry(null); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="email-history-view-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-200">
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('emailDetails') || 'Email Details'}</DialogTitle>
            <DialogDescription>
              {historyEntry?.sent_at ? new Date(historyEntry.sent_at).toLocaleString() : ''}
            </DialogDescription>
          </DialogHeader>
          {historyEntry && (
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('subject') || 'Subject'}</div>
                  <div className="font-semibold text-zinc-800" data-testid="email-history-view-subject">{historyEntry.subject || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('status') || 'Status'}</div>
                  <span className={`text-[10px] px-1.5 py-0.5 border font-semibold uppercase ${historyEntry.status === 'sent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                    {historyEntry.status || '—'}
                  </span>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('to') || 'To'}</div>
                  <div className="font-mono text-xs text-zinc-700 break-all" data-testid="email-history-view-to">{historyEntry.to_email || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('template') || 'Template'}</div>
                  <div className="text-zinc-700">{historyEntry.template_name || <span className="text-zinc-400 italic">—</span>}</div>
                </div>
                {(historyEntry.cc) && (
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">CC</div>
                    <div className="font-mono text-xs text-zinc-700 break-all">{historyEntry.cc}</div>
                  </div>
                )}
                {(historyEntry.bcc) && (
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">BCC</div>
                    <div className="font-mono text-xs text-zinc-700 break-all">{historyEntry.bcc}</div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('sentBy') || 'Sent By'}</div>
                  <div className="text-zinc-700">{historyEntry.sent_by || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('building') || 'Building'}</div>
                  <div className="text-zinc-700">{historyEntry.building_name || buildingName || '—'}</div>
                </div>
              </div>
              <div className="border-t border-zinc-200 pt-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t('body') || 'Body'}</div>
                <div
                  className="email-preview-body bg-white border border-zinc-200 p-4 text-sm max-h-[55vh] overflow-y-auto"
                  data-testid="email-history-view-body"
                  dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(historyEntry.body_html || historyEntry.body_quill || historyEntry.body || '') }}
                />
              </div>
              {/* Phase 347 — Show attachments that rode with this email. The
                  list shows filenames + sizes; clicking downloads the original
                  binary that was actually sent. */}
              {((historyEntry.attachments_meta || []).length > 0) && (
                <div className="border-t border-zinc-200 pt-3">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                    {t('attachments') || 'Attachments'} ({historyEntry.attachments_meta.length})
                  </div>
                  <ul className="border border-zinc-200 divide-y divide-zinc-100" data-testid="email-history-view-attachments">
                    {historyEntry.attachments_meta.map((a, idx) => (
                      <li key={`${a.filename}-${idx}`} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                        <Paperclip size={12} className="text-zinc-500" />
                        <a
                          href={`${API}/email-history/${historyEntry.log_id}/attachments/${idx}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 truncate underline text-zinc-800 hover:text-zinc-900"
                          title={a.filename}
                          data-testid={`email-history-view-attach-${idx}`}
                        >
                          {a.filename}
                        </a>
                        <span className="text-[10px] text-zinc-400 font-mono">{formatBytes(a.size_bytes)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {historyEntry.error && (
                <div className="border-t border-zinc-200 pt-3">
                  <div className="text-[10px] uppercase tracking-wider text-red-600 mb-1">{t('error') || 'Error'}</div>
                  <div className="bg-red-50 border border-red-200 p-2 text-xs text-red-700 break-all">{historyEntry.error}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="px-6 py-3 border-t border-zinc-200">
            <Button variant="outline" onClick={() => { setHistoryViewOpen(false); setHistoryEntry(null); }} className="rounded-none" data-testid="email-history-view-close">
              {t('close') || 'Close'}
            </Button>
            <Button
              onClick={() => handleResend(historyEntry)}
              disabled={!historyEntry}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800"
              data-testid="email-history-view-resend"
            >
              <PaperPlaneRight size={14} className="mr-1" /> {t('resend') || 'Re-send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== Send dialog — recipients + attachments (Phase 347) ====== */}
      <Dialog open={sendOpen} onOpenChange={(o) => { if (!o) { setSendOpen(false); } else { setSendOpen(true); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col" data-testid="email-tpl-send-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('sendEmail') || 'Send Email'}</DialogTitle>
            <DialogDescription>
              {t('sendEmailHintConfirm') || 'Add recipients & attachments. A final preview will appear before the email is actually sent.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1 overflow-y-auto">
            <div>
              <Label className="text-xs">{t('to') || 'To'} *</Label>
              <Input
                value={sendTo}
                onChange={(e) => setSendTo(e.target.value)}
                className="rounded-none mt-1"
                placeholder="alice@example.com, bob@example.com"
                data-testid="email-tpl-send-to"
              />
              <p className="text-[10px] text-zinc-400 mt-0.5">{t('commaSeparated') || 'Comma-separated.'}</p>
            </div>
            <div>
              <Label className="text-xs">CC</Label>
              <Input
                value={sendCc}
                onChange={(e) => setSendCc(e.target.value)}
                className="rounded-none mt-1"
                placeholder="cc@example.com"
                data-testid="email-tpl-send-cc"
              />
            </div>
            <div>
              <Label className="text-xs">BCC</Label>
              <Input
                value={sendBcc}
                onChange={(e) => setSendBcc(e.target.value)}
                className="rounded-none mt-1"
                placeholder="bcc@example.com"
                data-testid="email-tpl-send-bcc"
              />
            </div>

            {/* Phase 347 — Attachments uploader. Files stay client-side until
                the user confirms; they ride the multipart POST so they end up
                in both Brevo AND the audit log. */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">{t('attachments') || 'Attachments'}</Label>
                <label
                  className="text-[10px] underline text-zinc-600 hover:text-zinc-900 cursor-pointer"
                  data-testid="email-tpl-attach-btn"
                >
                  + {t('addFiles') || 'Add files'}
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => { addAttachments(e.target.files); e.target.value = ''; }}
                    data-testid="email-tpl-attach-input"
                  />
                </label>
              </div>
              {attachments.length === 0 ? (
                <p className="text-[11px] text-zinc-400 italic">
                  {t('noAttachmentsHint') || 'No files attached. PDF, Excel, Word, images and other common documents are supported.'}
                </p>
              ) : (
                <ul className="border border-zinc-200 divide-y divide-zinc-100 max-h-40 overflow-y-auto" data-testid="email-tpl-attach-list">
                  {attachments.map((a, idx) => (
                    <li key={`${a.name}-${idx}`} className="flex items-center gap-2 px-2 py-1.5 text-xs" data-testid={`email-tpl-attach-row-${idx}`}>
                      <Paperclip size={12} className="text-zinc-500 flex-shrink-0" />
                      <span className="flex-1 truncate" title={a.name}>{a.name}</span>
                      <span className="text-[10px] text-zinc-400 font-mono">{formatBytes(a.size)}</span>
                      <label
                        className="text-[10px] text-zinc-500 hover:text-zinc-900 underline cursor-pointer"
                        data-testid={`email-tpl-attach-replace-${idx}`}
                        title={t('replace') || 'Replace'}
                      >
                        {t('replace') || 'Replace'}
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) replaceAttachment(idx, f); e.target.value = ''; }}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        className="text-zinc-400 hover:text-red-600"
                        data-testid={`email-tpl-attach-remove-${idx}`}
                        title={t('remove') || 'Remove'}
                      >
                        <Trash size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} className="rounded-none" data-testid="email-tpl-send-cancel">
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={openConfirm}
              disabled={sending || !sendTo.trim()}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800"
              data-testid="email-tpl-send-review"
            >
              <Eye size={14} className="mr-1" />
              {t('reviewAndSend') || 'Review & Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== Final confirmation modal (Phase 347) ======
          Renders the resolved email with every placeholder replaced by the
          actual data the recipients will see, plus the list of staged
          attachments. ONLY after confirming here does the actual POST fire. */}
      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!sending) setConfirmOpen(o); }}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] flex flex-col p-0" data-testid="email-tpl-confirm-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-200">
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('confirmSendTitle') || 'Final preview'}</DialogTitle>
            <DialogDescription>
              {t('confirmSendHint') || 'This is exactly what the recipients will receive. Click Confirm Send to dispatch.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 text-sm">
            {confirmLoading ? (
              <p className="text-zinc-500 text-center py-12">{t('loading') || 'Loading…'}</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('to') || 'To'}</div>
                    <div className="font-mono text-xs text-zinc-800 break-all" data-testid="email-tpl-confirm-to">{sendTo || '—'}</div>
                  </div>
                  {sendCc && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">CC</div>
                      <div className="font-mono text-xs text-zinc-800 break-all" data-testid="email-tpl-confirm-cc">{sendCc}</div>
                    </div>
                  )}
                  {sendBcc && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">BCC</div>
                      <div className="font-mono text-xs text-zinc-800 break-all" data-testid="email-tpl-confirm-bcc">{sendBcc}</div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t('subject') || 'Subject'}</div>
                  <div className="text-sm font-semibold text-zinc-900" data-testid="email-tpl-confirm-subject">
                    {confirmSubject || <span className="text-zinc-400 italic">—</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t('body') || 'Body'}</div>
                  <div
                    className="email-preview-body bg-white border border-zinc-200 p-4 text-sm max-h-[55vh] overflow-y-auto"
                    data-testid="email-tpl-confirm-body"
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(confirmBody || '') }}
                  />
                </div>
                {attachments.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      {t('attachments') || 'Attachments'} ({attachments.length})
                    </div>
                    <ul className="border border-zinc-200 divide-y divide-zinc-100" data-testid="email-tpl-confirm-attachments">
                      {attachments.map((a, idx) => (
                        <li key={`${a.name}-${idx}`} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                          <Paperclip size={12} className="text-zinc-500" />
                          <span className="flex-1 truncate" title={a.name}>{a.name}</span>
                          <span className="text-[10px] text-zinc-400 font-mono">{formatBytes(a.size)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-zinc-200">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={sending}
              className="rounded-none"
              data-testid="email-tpl-confirm-cancel"
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={confirmAndSend}
              disabled={sending || confirmLoading}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800"
              data-testid="email-tpl-confirm-send"
            >
              <PaperPlaneRight size={14} className="mr-1" />
              {sending ? '…' : (t('confirmSend') || 'Confirm Send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
